import React, { useEffect, useState } from "react";
import { Loader2, Eye, FileDown, Layers, MessageSquare } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../../adminApi";
import { Card, SectionTitle } from "../../components/admin/AdminUI";

const RANGES = [
  { days: 7, label: "SON 7 GÜN" },
  { days: 30, label: "SON 30 GÜN" },
];

const Stat = ({ icon: Icon, label, value }) => (
  <Card className="p-6">
    <div className="flex items-center justify-between mb-7">
      <Icon size={18} strokeWidth={1.4} className="text-[#8C7862]" />
    </div>
    <p className="display text-[32px] lg:text-[38px] text-[#141414] leading-none">{value}</p>
    <p className="mono text-[8.5px] text-[#6B675F] mt-3">{label}</p>
  </Card>
);

const RankList = ({ title, rows, empty = "VERİ YOK", testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card className="p-6 lg:p-7" data-testid={testid}>
      <p className="mono text-[9px] text-[#6B675F] mb-5">{title}</p>
      {rows.length === 0 ? (
        <p className="mono text-[9px] text-[#A8A29A] py-6">{empty}</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.key}>
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[13.5px] text-[#141414] truncate">{r.title || r.key}</span>
                <span className="mono text-[9px] text-[#6B675F] shrink-0">{r.count}</span>
              </div>
              <div className="h-[3px] bg-[#EFECE6] mt-2">
                <div className="h-full bg-[#141414]" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.analytics(days).then(setData).catch(() => setData(null));
  }, [days]);

  const series = (data?.series || []).map((s) => ({ ...s, label: s.day.slice(5) }));
  const langs = data?.languages || [];
  const langTotal = langs.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div data-testid="admin-analytics">
      <SectionTitle
        label="[ ZİYARETÇİ ANALİTİĞİ ]"
        title="Site istatistikleri"
        right={
          <div className="flex gap-2">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`analytics-range-${r.days}`}
                className={`mono text-[9px] px-4 py-2.5 border transition-colors ${
                  days === r.days
                    ? "bg-[#141414] text-[#FAFAFA] border-[#141414]"
                    : "bg-white text-[#141414] border-[#E2E0D8] hover:border-[#C8B29B]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      {!data ? (
        <Loader2 size={18} className="animate-spin text-[#8C7862]" />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Eye} label="SAYFA GÖRÜNTÜLEME" value={data.totals.pages} />
            <Stat icon={Layers} label="PROJE GÖRÜNTÜLEME" value={data.totals.projects} />
            <Stat icon={FileDown} label="PDF İNDİRME" value={data.totals.pdfs} />
            <Stat icon={MessageSquare} label={`MESAJ (${data.totals.unread} OKUNMAMIŞ)`} value={data.totals.enquiries} />
          </div>

          <Card className="p-6 lg:p-7">
            <p className="mono text-[9px] text-[#6B675F] mb-6">GÜNLÜK HAREKET</p>
            <div className="h-[240px]" data-testid="analytics-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series}>
                  <CartesianGrid stroke="#EFECE6" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: "#6B675F", fontFamily: "monospace" }}
                    axisLine={{ stroke: "#E2E0D8" }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#6B675F", fontFamily: "monospace" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    width={28}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 0,
                      border: "1px solid #E2E0D8",
                      fontSize: 12,
                      fontFamily: "monospace",
                    }}
                    labelStyle={{ color: "#6B675F" }}
                  />
                  <Bar dataKey="count" fill="#141414" radius={0} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RankList title="EN ÇOK GÖRÜNTÜLENEN PROJELER" rows={data.topProjects} testid="analytics-projects" />
            <RankList title="EN ÇOK GÖRÜNTÜLENEN KOLEKSİYONLAR" rows={data.topCollections} testid="analytics-collections" />
            <RankList title="EN ÇOK İNDİRİLEN PDF'LER" rows={data.topPdfs} testid="analytics-pdfs" />
            <RankList title="EN ÇOK ZİYARET EDİLEN SAYFALAR" rows={data.topPages} testid="analytics-pages" />
          </div>

          <Card className="p-6 lg:p-7" data-testid="analytics-languages">
            <p className="mono text-[9px] text-[#6B675F] mb-5">DİL DAĞILIMI</p>
            {langs.length === 0 ? (
              <p className="mono text-[9px] text-[#A8A29A] py-4">VERİ YOK</p>
            ) : (
              <div className="flex flex-wrap gap-8">
                {langs.map((l) => (
                  <div key={l.key}>
                    <p className="display text-[30px] text-[#141414] leading-none">
                      {Math.round((l.count / langTotal) * 100)}%
                    </p>
                    <p className="mono text-[8.5px] text-[#6B675F] mt-2">
                      {(l.key || "").toUpperCase()} · {l.count}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
